import React, { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import eklaseLogoPrimary from '@/assets/images/eklase-logo-small-primary.webp';
import eklaseLogo from '@/assets/images/eklase-logo-small.webp';
import somaLogo from '@/assets/images/soma-logo-small.webp';
import uzdevumiLogoDisabled from '@/assets/images/uzdevumi-logo-small-disabled.webp';
import uzdevumiLogo from '@/assets/images/uzdevumi-logo-small.webp';
import { ChevronRightFilled } from '@/components/Icons/ChevronRightFilled';
import { HomeTaskType } from '@/types/components';
import { getFirstParagraph, sanitizeRichTextHtml } from '@/utils/html';
import { tv } from '@/utils/tv';

const EKLASE_LOGO_SIZE = 'h-[14px] w-6 shrink-0';
const UZDEVUMI_LOGO_SIZE = 'h-[15px] w-[84px] shrink-0';

const chipLogos: Record<HomeTaskType, ReactNode> = {
  [HomeTaskType.EKlase]: (
    <img src={eklaseLogo} alt="" className={EKLASE_LOGO_SIZE} />
  ),
  [HomeTaskType.Soma]: (
    <img src={somaLogo} alt="" className="h-5 w-[71px] shrink-0" />
  ),
  [HomeTaskType.Uzdevumi]: (
    <img src={uzdevumiLogo} alt="" className={UZDEVUMI_LOGO_SIZE} />
  ),
};

const answeredChipLogo = (
  <img src={eklaseLogoPrimary} alt="" className={EKLASE_LOGO_SIZE} />
);

const deletedChipLogo = (
  <img src={uzdevumiLogoDisabled} alt="" className={UZDEVUMI_LOGO_SIZE} />
);

const chip = tv({
  base: 'flex h-7 items-center justify-between gap-2 rounded-[20px] py-1 pl-3',
  variants: {
    type: {
      [HomeTaskType.EKlase]: 'bg-primary text-primary-foreground',
      [HomeTaskType.Soma]: 'bg-partner-bag text-constant-black',
      [HomeTaskType.Uzdevumi]: 'bg-partner-tasks text-white',
    },
    isAnswered: { true: '' },
    isDeleted: {
      true: 'bg-on-primary-surface pr-3 text-muted-foreground',
      false: 'pr-2',
    },
  },
  compoundVariants: [
    {
      type: HomeTaskType.EKlase,
      isAnswered: true,
      class: 'bg-info-surface text-primary',
    },
  ],
});

const CARD =
  'flex w-full flex-col gap-2 rounded-[10px] bg-card p-3 text-left shadow-card contrast:border contrast:border-border';

interface Props {
  type: HomeTaskType;
  text: string;
  hasStudentAnswer?: boolean;
  isDeleted?: boolean;
  onClick?: () => void;
}

export const HomeTaskCard: React.FC<Props> = ({
  type,
  text,
  hasStudentAnswer = false,
  isDeleted = false,
  onClick,
}) => {
  const { t } = useTranslation();
  const isEklase = type === HomeTaskType.EKlase;
  const isAnswered = isEklase && hasStudentAnswer;
  const isDeletedUzdevumi = isDeleted && type === HomeTaskType.Uzdevumi;
  const isClickable = !!onClick && !isDeleted;
  const description = isEklase ? getFirstParagraph(text) : text;

  const content = (
    <>
      <div className={chip({ type, isAnswered, isDeleted })}>
        <span className="flex min-w-0 items-center gap-1">
          {isAnswered && answeredChipLogo}
          {isDeletedUzdevumi && deletedChipLogo}
          {!isAnswered && !isDeletedUzdevumi && chipLogos[type]}

          {isEklase && (
            <span className="truncate text-[13px]/[18px] font-semibold">
              {hasStudentAnswer && t('diary.homeTask.viewAnswers')}
              {!hasStudentAnswer && t('diary.homeTask.submitAnswers')}
            </span>
          )}
        </span>

        {isDeleted && (
          <span className="text-caption-12">{t('diary.homeTask.deleted')}</span>
        )}
        {!isDeleted && <ChevronRightFilled className="size-4 shrink-0" />}
      </div>

      {!isDeleted && !description && (
        <p className="text-body-14 text-foreground">
          {t('diary.emptyDescription')}
        </p>
      )}
      {!isDeleted && !!description && isEklase && (
        <div
          className="rich-text text-body-14 line-clamp-1 text-foreground"
          dangerouslySetInnerHTML={{
            __html: sanitizeRichTextHtml(description),
          }}
        />
      )}
      {!isDeleted && !!description && !isEklase && (
        <p className="text-body-14 text-foreground">{description}</p>
      )}
    </>
  );

  if (isClickable) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${CARD} cursor-pointer`}
      >
        {content}
      </button>
    );
  }

  return <div className={CARD}>{content}</div>;
};
